import type { Interceptor, Transport } from '@connectrpc/connect'
import { createGrpcWebTransport } from '@connectrpc/connect-web'
import { runtimeConfig } from '@/config/runtime'
import { acceptLanguageInterceptor, createAuthInterceptor } from './interceptors'
import { useSession } from '@/auth/session'

/**
 * One gRPC-Web transport per backend host. The services are split across
 * three ingresses and a call to the wrong one is a 404, so every client in
 * clients.ts names its host explicitly:
 *
 *   auth      WebSessionService, CrewMapService, PlayerService
 *   building  WorldMapService, BuildingService, NpcService (quest givers)
 *   npc       PoliceService, RoamerService
 */
export type ApiHost = 'auth' | 'building' | 'npc'

type RefreshHandler = (usedToken: string | null) => Promise<string | null>

let refreshHandler: RefreshHandler = async () => null

/** Wired once by src/auth/actions.ts; kept here to avoid an import cycle. */
export function setRefreshHandler(fn: RefreshHandler): void {
  refreshHandler = fn
}

export const authInterceptor: Interceptor = createAuthInterceptor({
  getAccessToken: () => useSession.getState().accessToken,
  refresh: (used) => refreshHandler(used),
})

export const defaultInterceptors: Interceptor[] = [acceptLanguageInterceptor, authInterceptor]

type TransportFactory = (host: ApiHost) => Transport

let factory: TransportFactory | null = null
const cache = new Map<ApiHost, Transport>()
let generation = 0

/** Demo mode / tests swap every host for an in-memory router transport. */
export function setTransportFactory(f: TransportFactory | null): void {
  factory = f
  cache.clear()
  generation++
}

/** Bumps whenever the transports are swapped, so clients can rebuild. */
export function transportGeneration(): number {
  return generation
}

function baseUrlFor(host: ApiHost): string {
  const cfg = runtimeConfig()
  switch (host) {
    case 'auth':
      return cfg.authApiUrl
    case 'building':
      return cfg.buildingApiUrl
    case 'npc':
      return cfg.npcApiUrl
  }
}

export function getTransport(host: ApiHost): Transport {
  let t = cache.get(host)
  if (!t) {
    t = factory
      ? factory(host)
      : createGrpcWebTransport({
          baseUrl: baseUrlFor(host),
          useBinaryFormat: true,
          interceptors: defaultInterceptors,
          defaultTimeoutMs: 20_000,
        })
    cache.set(host, t)
  }
  return t
}
